import type { ProjectDetail } from '@/stores/project/types';
import { getAnonymousCredits } from '@/services/pay';
import { getProjectDetail } from '@/services/project';
import { useLoginModalStore } from '@/stores/loginModal';
import { useVisitorCreditsStore } from '@/stores/visitorCredits';

export type VisitorCreditsCheckFailReason = 'INSUFFICIENT_CREDITS' | 'UNKNOWN';

export type VisitorCreditsCheckResult
  = { ok: true; reasonCode?: undefined }
    | { ok: false; reasonCode: VisitorCreditsCheckFailReason };

/**
 * 匿名用户积分 store 的积分刷新
 */
export function syncVisitorCreditsFromDetail(detail: Pick<ProjectDetail, 'remainingCredits'>): void {
  if (typeof detail.remainingCredits !== 'number') {
    return;
  }
  useVisitorCreditsStore.getState().setUserPoints(detail.remainingCredits);
}

/**
 * 匿名身份初始化后，从匿名积分接口刷新剩余积分。
 * 请求失败时保留 store 当前值（首次初始化即默认 100）。
 */
export async function refreshAnonymousCredits(): Promise<number> {
  try {
    const { data } = await getAnonymousCredits();
    useVisitorCreditsStore.getState().setUserPoints(data.remainingCredits);
    return data.remainingCredits;
  } catch (error) {
    console.warn('refresh anonymous credits failed:', error);
    return useVisitorCreditsStore.getState().userPoints;
  }
}

/**
 * 匿名用户积分 store 的积分从项目详情接口拉取并刷新
 */
export async function refreshVisitorCredits(projectId: string): Promise<number | null> {
  const { data } = await getProjectDetail(projectId);
  syncVisitorCreditsFromDetail(data);
  return typeof data.remainingCredits === 'number' ? data.remainingCredits : null;
}

/**
 * 匿名用户发送消息前的实时积分校验
 * - 积分耗尽：弹登录弹窗，返回 INSUFFICIENT_CREDITS
 * - 接口异常：返回 UNKNOWN
 */
export async function ensureVisitorCreditsBeforeSend(projectId: string): Promise<VisitorCreditsCheckResult> {
  try {
    const remaining = await refreshVisitorCredits(projectId);
    // Some owned/local projects do not expose remainingCredits in project detail.
    // Missing data is not proof of an empty balance; the queue API remains the
    // authoritative guard and will reject the send if the account cannot pay.
    if (remaining === null) {
      return { ok: true };
    }
    if (remaining <= 0) {
      useLoginModalStore.getState().open();
      return { ok: false, reasonCode: 'INSUFFICIENT_CREDITS' };
    }
    return { ok: true };
  } catch {
    return { ok: false, reasonCode: 'UNKNOWN' };
  }
}
